<template>
  <div
    v-if="isBillingEnabled"
    :class="[
      'mt-5',
      'subscription-manager',
      'subscription-manager-shell',
      { 'subscription-manager-shell--purchase': isPurchaseOpen }
    ]"
  >
    <div v-if="!isPurchaseOpen" class="manager-view">
      <div class="manager-hero">
        <div class="manager-copy">
          <div class="manager-copy-main">
            <div>
              <p class="manager-kicker">Subscription</p>
              <h2 class="manager-title">Manage Subscription</h2>
              <p class="manager-subtitle text-muted">
                Choose the plan that works for you.
              </p>
            </div>
            <div class="manager-actions">
              <v-btn
                color="primary"
                class="action-btn"
                outlined
                @click="changePlan"
              >
                <v-icon left small>mdi-swap-horizontal</v-icon>
                Change Plan
              </v-btn>
              <v-btn
                color="primary"
                class="action-btn"
                depressed
                @click="openBillingPortal"
              >
                <v-icon left small>mdi-credit-card-outline</v-icon>
                Manage Billing
              </v-btn>
            </div>
          </div>
          <div class="hero-meta">
            <span :class="['status-pill', `status-pill--${statusTone}`]">
              {{ subscriptionStatusLabel }}
            </span>
            <span class="plan-label">{{ currentPlanLabel }}</span>
            <span class="text-muted">{{ billingCycleLabel }}</span>
          </div>
        </div>
      </div>

      <div class="card shadow-sm p-4 manage-subscription-card" v-if="!loading">
        <div class="row">
          <div class="col-md-4 mb-3 mb-md-0">
            <div class="p-3 rounded subscription-panel">
              <h5 class="font-weight-bold panel-title">Usage Details</h5>
              <div class="stat-line">
                <span>Included</span>
                <strong>{{ formatGB(usageDetails.memoryIncluded) }} GB</strong>
              </div>
              <div class="stat-line">
                <span>Bonus</span>
                <strong>{{ formatGB(usageDetails.memoryBonus) }} GB</strong>
              </div>
              <div class="stat-line">
                <span>Used</span>
                <strong>{{ formatGB(usageDetails.memoryUsed) }} GB</strong>
              </div>
              <div class="stat-line stat-line--total">
                <span>Total Capacity</span>
                <strong>{{ formatGB(usageDetails.memoryTotal) }} GB</strong>
              </div>

              <v-progress-linear
                v-if="usageDetails.memoryTotal !== '0.00'"
                :value="Number(usageDetails.memoryUsagePercentage)"
                :color="memoryProgressColor"
                height="14"
                rounded
                class="mt-3"
              />
              <p class="usage-caption mt-2">
                {{ usageDetails.memoryUsagePercentage }}% used
              </p>

              <div class="quota-block mt-4">
                <h6 class="quota-title">Monthly Link Quotas</h6>
                <div class="stat-line">
                  <span>Share links</span>
                  <strong>{{ shareQuotaLabel }}</strong>
                </div>
                <div class="stat-line">
                  <span>Drop links</span>
                  <strong>{{ dropQuotaLabel }}</strong>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-4 mb-3 mb-md-0">
            <div class="p-3 rounded subscription-panel">
              <h5 class="font-weight-bold panel-title">Plan Features</h5>
              <ul class="feature-list list-unstyled mb-0">
                <li v-for="(feature, index) in planFeatures" :key="index">
                  <v-icon small color="success" class="mr-2">
                    mdi-check-circle-outline
                  </v-icon>
                  <span>{{ feature }}</span>
                </li>
              </ul>
            </div>
          </div>

          <div class="col-md-4">
            <div class="p-3 rounded subscription-panel">
              <h5 class="font-weight-bold panel-title">Billing & Payment</h5>
              <div v-if="hasBillingDetails">
                <div class="stat-line">
                  <span>Price</span>
                  <strong>
                    {{ formatCurrency(billing.price) }}
                    / {{ billing.interval === "Monthly" ? "mo" : "year" }}
                  </strong>
                </div>
                <div class="stat-line">
                  <span>Billing period</span>
                  <strong>{{ billing.interval }}</strong>
                </div>
                <div class="stat-line">
                  <span>Renewal date</span>
                  <strong>{{ billing.renewalDate }}</strong>
                </div>
              </div>
              <div v-else class="billing-empty">
                <p class="billing-empty-title mb-1">Billing Details Unavailable</p>
                <p class="text-muted mb-0">
                  This account may be managed outside Stripe portal sync.
                </p>
              </div>
              <v-divider class="my-3" />
              <v-btn
                block
                color="primary"
                outlined
                class="mb-2 billing-action-btn"
                @click="openBillingPortal"
              >
                {{ hasBillingDetails ? "Edit Billing" : "Open Billing Portal" }}
              </v-btn>
              <v-btn
                block
                class="billing-action-btn"
                color="primary"
                depressed
                @click="openBillingPortal"
              >
                View Invoices
              </v-btn>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading" class="text-center mt-4">
        <span class="spinner-border text-primary"></span> Loading subscription
        details...
      </div>
      <div v-if="error" class="alert alert-danger text-center mt-4">
        {{ error }}
      </div>
    </div>
    <PurchaseSubscription
      v-if="isPurchaseOpen"
      @close="handleClosePurchase()"
      :currentPriceId="currentPriceId"
      :subscriptionTier="effectiveSubscriptionTier"
    />
  </div>
</template>

<script>
import Vue from "vue";
import PurchaseSubscription from "@/components/PurchaseSubscription.vue";
import { EventBus } from "@/js/eventBus";
import { mapGetters } from "vuex";

export default {
  components: { PurchaseSubscription },
  props: {
    showPurchase: { type: Boolean }
  },
  data() {
    return {
      // showPurchase: false,
      loading: true,
      error: null,
      user: this.$store.state.auth.user, // Get user from Vuex store
      currentPriceId: null,
      usageDetails: {
        included: 0,
        total: 0,
        rollover: 0,
        bonus: 0
      },
      plans: [],
      planFeatures: [],
      purchaseDialogOpen: false,
      syncedSubscriptionTier: null,
      syncedSubscriptionStatus: null,
      billing: {
        price: 0,
        renewalDate: "N/A"
      }
    };
  },
  computed: {
    ...mapGetters("billing", [
      "catalog",
      "isBillingEnabled",
      "quotaUsage",
      "quotaLimits",
      "quotaRemaining"
    ]),
    catalogPaidPlans() {
      const tiers = this.catalog?.tiers || [];
      return tiers
        .filter(tier => tier.id === "base" || tier.id === "pro")
        .map(tier => ({
          name: tier.label,
          stripePriceIdMonthly: tier.price?.price_id?.monthly || "",
          stripePriceIdYearly: tier.price?.price_id?.yearly || "",
          features: tier.features || []
        }));
    },
    shareQuotaLabel() {
      const limit = this.quotaLimits?.share_links;
      const used = this.quotaUsage?.share_links || 0;
      if (limit === -1) return "Unlimited";
      return `${used}/${limit} used`;
    },
    dropQuotaLabel() {
      const limit = this.quotaLimits?.drop_links;
      const used = this.quotaUsage?.drop_links || 0;
      if (limit === -1) return "Unlimited";
      return `${used}/${limit} used`;
    },
    currentPlanLabel() {
      const tier = this.effectiveSubscriptionTier;
      if (tier === "base") return "Basic Plan";
      if (tier === "pro") return "Pro Plan";
      if (tier === "enterprise") return "Enterprise Plan";
      return "Free Plan";
    },
    subscriptionStatusLabel() {
      return (this.effectiveSubscriptionStatus || "free").toUpperCase();
    },
    billingCycleLabel() {
      if (!this.billing.interval) {
        if (this.effectiveSubscriptionStatus === "active") {
          return "";
        }
        return "No active billing cycle";
      }
      return `Billed ${this.billing.interval.toLowerCase()}`;
    },
    statusTone() {
      const status = this.effectiveSubscriptionStatus;
      if (status === "active") return "active";
      if (status === "past_due") return "warning";
      if (status === "canceled" || status === "canceled_pending") return "danger";
      return "neutral";
    },
    hasBillingDetails() {
      const hasInterval = Boolean(this.billing.interval);
      const hasRenewal =
        Boolean(this.billing.renewalDate) && this.billing.renewalDate !== "N/A";
      const hasPrice = Number(this.billing.price) > 0;
      return hasInterval || hasRenewal || hasPrice;
    },
    memoryProgressColor() {
      const pct = Number(this.usageDetails.memoryUsagePercentage || 0);
      if (pct >= 90) return "error";
      if (pct >= 70) return "warning";
      return "success";
    },
    currentTierCatalog() {
      const tiers = this.catalog?.tiers || [];
      return tiers.find(tier => tier.id === this.effectiveSubscriptionTier) || null;
    },
    effectiveSubscriptionTier() {
      return (
        this.syncedSubscriptionTier ||
        (this.user?.subscription_tier || "free").toLowerCase()
      );
    },
    effectiveSubscriptionStatus() {
      return (
        this.syncedSubscriptionStatus ||
        (this.user?.subscription_status || "").toLowerCase()
      );
    },
    isPurchaseOpen() {
      return Boolean(this.showPurchase || this.purchaseDialogOpen);
    }
  },
  methods: {
    openPurchaseDialog() {
      this.purchaseDialogOpen = true;
    },
    handleClosePurchase() {
      if (this.purchaseDialogOpen) {
        this.purchaseDialogOpen = false;
      } else {
        this.$emit("close");
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return "N/A";
      return new Date(timestamp * 1000).toLocaleString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      });
    },
    formatGB(bytes) {
      if (typeof bytes !== "number" || isNaN(bytes) || bytes <= 0)
        return "0.00"; // Ensure valid input
      return `${(bytes / 1024 ** 3).toFixed(2)}`; // Convert bytes → GB with 2 decimal places
    },
    formatCurrency(value) {
      const amount = Number(value);
      if (isNaN(amount) || amount <= 0) return "€0";
      return `€${amount}`;
    },
    setUsageDetails(memoryIncludedRaw, memoryBonusRaw, memoryUsedRaw) {
      this.usageDetails.memoryIncluded = memoryIncludedRaw;
      this.usageDetails.memoryBonus = memoryBonusRaw;
      this.usageDetails.memoryUsed = memoryUsedRaw;
      this.usageDetails.memoryTotal = memoryIncludedRaw + memoryBonusRaw;
      this.usageDetails.memoryUsagePercentage =
        this.usageDetails.memoryTotal > 0
          ? (
              (this.usageDetails.memoryUsed / this.usageDetails.memoryTotal) *
              100
            ).toFixed(2)
          : 0;
    },
    hydrateLocalUsageDetails() {
      const tierStorage = Number(this.currentTierCatalog?.storage_bytes || 0);
      const memoryIncludedRaw = Number(this.user?.memory_included || tierStorage);
      const memoryBonusRaw = Number(this.user?.memory_bonus || 0);
      const memoryUsedRaw = Number(this.user?.memory_used || 0);
      this.setUsageDetails(memoryIncludedRaw, memoryBonusRaw, memoryUsedRaw);
    },

    async fetchSubscriptionDetails() {
      try {
        this.loading = true;
        if (!this.user?.stripe_subscription_id) {
          this.planFeatures = this.currentTierCatalog?.features || [];
          this.hydrateLocalUsageDetails();
          this.loading = false;
          return;
        }
        const response = await Vue.$http.get("api/subscription-details");
        const data = response?.data || response?.body || response;

        this.syncedSubscriptionTier = (data.subscription_tier || "free").toLowerCase();
        this.syncedSubscriptionStatus = (data.status || "").toLowerCase();
        this.currentPriceId = data.price_id;

        // Update billing details
        this.billing.price = data.price;
        this.billing.billingPeriod = this.formatDate(data.billing_period);
        this.billing.renewalDate = this.formatDate(data.renewal_date);
        this.billing.interval =
          data.billing_interval === "month" ? "Monthly" : "Yearly";

        // Update usage details (if applicable)
        if (!data || Object.keys(data).length === 0) {
          console.warn("[WARNING] No subscription details received.");
          this.loading = false;
          return;
        }

        this.setUsageDetails(
          Number(data.memory_included || 0),
          Number(data.memory_bonus || 0),
          Number(data.memory_used || 0)
        );

        const selectedPlan = this.catalogPaidPlans.find(
          plan =>
            plan.stripePriceIdMonthly === this.currentPriceId ||
            plan.stripePriceIdYearly === this.currentPriceId
        );

        this.planFeatures = selectedPlan ? selectedPlan.features : [];

        this.loading = false;
      } catch (err) {
        this.planFeatures = this.currentTierCatalog?.features || [];
        this.hydrateLocalUsageDetails();
        console.error("Failed to fetch subscription details.");
        this.loading = false;
      }
    },
    async cancelPlan() {
      try {
        // TODO ask for hard confirmation (like write "cancel subscription")
        const response = await this.$http.post("/api/cancel-subscription"); // Use 'this' instead of 'Vue'

        if (response.status === 200) {
          // Cancellation is deferred to period end and completed by webhook sync.
          this.syncedSubscriptionStatus = "canceled_pending";
          alert("Your subscription will be canceled at the end of the billing period.");
        } else {
          alert("Failed to cancel subscription. Please try again.");
        }
      } catch (error) {
        console.error("Error canceling subscription:", error);
        alert("An error occurred while canceling the subscription."); // TODO show a dialog instead
      }
    },
    changePlan() {
      const tier = this.effectiveSubscriptionTier;
      if (tier === "free" || !this.user?.stripe_subscription_id) {
        this.purchaseDialogOpen = true;
        return;
      }
      this.openBillingPortal();
    },
    async openBillingPortal() {
      try {
        const response = await this.$http.post("api/billing-portal", {
          customerId: this.user.stripeCustomerId
        });

        const payload = response?.data || response?.body || response;
        if (payload?.url) {
          window.location.href = payload.url; // Redirect to Stripe billing portal
        } else {
          console.error("Failed to get billing URL");
        }
      } catch (error) {
        console.error("Billing portal error:", error);
      }
    }
  },
  mounted() {
    EventBus.$on("subscription-panel-opened", this.fetchSubscriptionDetails);
    this.$store
      .dispatch("billing/fetchBillingCatalog")
      .finally(() => this.fetchSubscriptionDetails());
  }
};
</script>

<style scoped>
.subscription-manager {
  color: #f5f5f5;
}
.subscription-manager-shell {
  max-width: 980px;
  margin: 0 auto;
}
.subscription-manager-shell--purchase {
  max-width: 1040px;
}
.manager-view {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.manager-hero {
  display: block;
}
.manager-copy {
  background: #1e1e1e !important;
  border: 1px solid #2f333a;
  border-radius: 16px;
  padding: 18px;
}
.manager-copy-main {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}
.manager-kicker {
  margin-bottom: 6px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 12px;
  color: #b2b7c0;
}
.manager-title {
  margin: 0 0 4px;
  font-weight: 700;
}
.manager-subtitle {
  margin: 0;
}
.hero-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.12);
}
.status-pill {
  display: inline-flex;
  width: fit-content;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid transparent;
}
.status-pill--active {
  color: #5ce5a8;
  background: rgba(92, 229, 168, 0.12);
  border-color: rgba(92, 229, 168, 0.35);
}
.status-pill--warning {
  color: #f4c15d;
  background: rgba(244, 193, 93, 0.12);
  border-color: rgba(244, 193, 93, 0.35);
}
.status-pill--danger {
  color: #ff8f87;
  background: rgba(255, 143, 135, 0.12);
  border-color: rgba(255, 143, 135, 0.35);
}
.status-pill--neutral {
  color: #cfd7e6;
  background: rgba(207, 215, 230, 0.12);
  border-color: rgba(207, 215, 230, 0.35);
}
.plan-label {
  font-size: 16px;
  font-weight: 700;
  color: #f5f7fb;
}
.manager-actions {
  display: flex;
  justify-content: flex-start;
  gap: 10px;
}
.action-btn {
  min-width: 160px;
  border-radius: 10px !important;
}
.manage-subscription-card {
  background: #1e1e1e;
  border-radius: 16px;
  border: none;
}
.subscription-panel {
  height: 100%;
  background: #2a2a2a !important;
  border: none;
  color: #f5f5f5;
  border-radius: 14px !important;
}
.billing-action-btn {
  border-radius: 10px !important;
}
.panel-title {
  margin-bottom: 14px;
}
.stat-line {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  font-size: 13px;
  color: #cfd2d8;
  margin-bottom: 8px;
}
.stat-line strong {
  color: #ffffff;
  font-size: 14px;
}
.stat-line--total {
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid #3a3f49;
}
.usage-caption {
  color: #9fa6b4;
  font-size: 12px;
}
.quota-block {
  border-top: 1px solid #3a3f49;
  padding-top: 12px;
}
.quota-title {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #b2b7c0;
  margin-bottom: 10px;
}
.feature-list li {
  display: flex;
  align-items: flex-start;
  gap: 2px;
  margin-bottom: 10px;
  font-size: 13px;
  line-height: 1.4;
}
.feature-list {
  max-height: 320px;
  overflow: auto;
  padding-right: 4px;
}
.billing-empty {
  border: 1px dashed rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.03);
}
.billing-empty-title {
  color: #e8ecf4;
  font-weight: 600;
}
.text-muted {
  color: #bdbdbd !important;
}
.spinner-border {
  width: 2rem;
  height: 2rem;
}
@media (max-width: 900px) {
  .manager-copy-main {
    flex-direction: column;
  }
  .manager-actions {
    width: 100%;
  }
  .action-btn {
    flex: 1;
    min-width: 0;
  }
}
@media (max-width: 640px) {
  .manager-actions {
    flex-direction: column;
  }
}
</style>
